import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { useToast } from "../ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Spinner } from "../ui/Spinner";
import { subModuleService } from "../../services/subModuleService";
import { SubModule } from "types/subModule";

interface MarkupSubModule {
  id: number;
  module_id: number;
  name: string;
  type: string;
  content: string;
  video_url: string;
  sequence_number: number;
  points_awarded: number;
  start_date?: string;
  end_date?: string;
}

interface MarkupTableProps {
  moduleId?: string;
  isActive: boolean;
}

export function MockupTable({ moduleId, isActive }: MarkupTableProps) {
  const { toast } = useToast();
  const [modules, setModules] = useState<MarkupSubModule[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchModules = async () => {
      if (!isActive || !moduleId) {
        setModules([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data: SubModule[] = await subModuleService.getSubModulesByModuleId(parseInt(moduleId), "mock_call");
        const mapped = data.map((item) => ({
          id: Number(item.id),
          module_id: Number(item.moduleId),
          name: item.name || "",
          type: item.type || "",
          content: item.content || "",
          video_url: item.videoUrl || "",
          sequence_number: item.sequenceNumber ?? 0,
          points_awarded: item.pointsAwarded ?? 0,
          start_date: item.startDate,
          end_date: item.endDate,
        }));
        setModules(mapped);
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to fetch mockup modules",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    if (isActive && moduleId) {
      fetchModules();
    }
    if (!isActive) {
      setModules([]);
    }
  }, [isActive, moduleId, toast]);

  if (loading) {
    return <Spinner label="Loading..." />;
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Seq</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Content</TableHead>
            <TableHead>Points</TableHead>
            <TableHead>Start Date</TableHead>
            <TableHead>End Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {modules.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground">
                No mockup submodules found.
              </TableCell>
            </TableRow>
          ) : (
            modules.map((module) => (
              <TableRow key={module.sequence_number + '-' + module.name}>
                <TableCell>{module.sequence_number}</TableCell>
                <TableCell>{module.name}</TableCell>
                <TableCell>{module.type}</TableCell>
                <TableCell>{module.content}</TableCell>
                <TableCell>{module.points_awarded}</TableCell>
                <TableCell>{module.start_date ? module.start_date.split('T')[0] : ''}</TableCell>
                <TableCell>{module.end_date ? module.end_date.split('T')[0] : ''}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
